// Shop API functions
import { getUserInfo } from './auth';
import { CommonUtils, parseApiCountMap } from './commonUtils';
import { getApiV1Url, getCdnBaseUrl } from '../config/api';
import {
  resolveStoredFileUrl,
  requestPresignedDownloads,
  appendCacheVersionQuery,
  toCdnCacheVersion,
  type UploadFilePayload,
} from './storage';
import { generateAfter } from './fractionalIndex';

// ============================================================================
// INTERFACES
// ============================================================================

// 기본 매장 정보
export interface Shop {
  projectCode: string;
  code: string;
  outletCode: string;
  status: string;
  number: number;
  type: string;
  name: string;
  brandName?: string;
  shortName?: string;
  salesTime?: string;
  comment?: string;
  intro?: string;
  phoneNumber: string;
  ownerPhoneNumber?: string;
  homepage?: string;
  postalCode?: string;
  regionCode?: string;
  addressRoad: string;
  addressDetail: string;
  lat: number;
  lon: number;
  /** 대표 파일 버전 (CDN `?v=` 우선 사용, API Long / DB bigint) */
  primaryVersion?: number | null;
  /** 리워드 카드 파일 버전 (CDN `?v=` 우선 사용) */
  rewardCardVersion?: string | number | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

// 매장 상세 정보 (Shop + loginPassword)
export interface ShopDetail extends Shop {
  brandCode?: string; // API 응답에 포함될 수 있음
  loginPassword?: string;
}

// 매장 생성 요청
export interface ShopCreateRequest {
  outletCode: string;
  status: string;
  number: number;
  type: string;
  name: string;
  brandCode?: string;
  shortName?: string;
  salesTime?: string;
  comment?: string;
  intro?: string;
  phoneNumber: string;
  ownerPhoneNumber?: string;
  homepage?: string;
  postalCode?: string;
  regionCode?: string;
  addressRoad: string;
  addressDetail: string;
  lat: number;
  lon: number;
  loginPassword?: string;
}

// 매장 수정 요청
export interface ShopUpdateRequest {
  outletCode: string;
  status: string;
  number: number;
  type: string;
  name: string;
  brandCode?: string;
  shortName?: string;
  salesTime?: string;
  comment?: string;
  intro?: string;
  phoneNumber: string;
  ownerPhoneNumber?: string;
  homepage?: string;
  postalCode?: string;
  regionCode?: string;
  addressRoad: string;
  addressDetail: string;
  lat: number;
  lon: number;
  loginPassword?: string;
}

// 매장 목록 요청
export interface ShopListRequest {
  page?: number;
  size?: number;
  sort?: string;
  status?: string;
  keyword?: string; // 검색 키워드 (매장명, 매장코드, 매장번호, 지역코드)
  brandCode?: string; // 브랜드 코드
  includeSubBrands?: boolean; // 하위 브랜드의 매장도 포함할지 여부
  view?: string;
}

// 매장 목록 응답
export interface ShopListResponse {
  totalCount: number;
  shops: Shop[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// 매장 생성 응답
export interface ShopCreateResponse {
  success: boolean;
  message: string;
  data?: {
    code: string;
    name: string;
    status: string;
    createdAt: string;
  };
}

// 매장 수정 응답
export interface ShopUpdateResponse {
  success: boolean;
  message: string;
  data?: {
    code: string;
    name: string;
    status: string;
    updatedAt: string;
  };
}

// 매장 상태별 건수 응답
export interface ShopStatusCount {
  count: number;
  ACTIVE: number;
  INACTIVE: number;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

// 매장 생성 API
export const createShop = async (
  projectCode: string, 
  shopData: ShopCreateRequest
): Promise<ShopCreateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/shops`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(shopData)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return {
      success: true,
      message: '매장이 성공적으로 생성되었습니다.',
      data: result
    };
  } catch (error: any) {
    console.error('매장 생성 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 매장 목록 조회 API
export const getShopList = async (
  projectCode: string,
  request: ShopListRequest
): Promise<ShopListResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const queryParams = new URLSearchParams();
    if (request.page !== undefined) queryParams.append('page', request.page.toString());
    if (request.size !== undefined) queryParams.append('size', request.size.toString());
    if (request.sort) queryParams.append('sort', request.sort);
    if (request.status) queryParams.append('status', request.status);
    if (request.keyword) queryParams.append('keyword', request.keyword);
    if (request.brandCode) queryParams.append('brandCode', request.brandCode);
    if (request.includeSubBrands) queryParams.append('includeSubBrands', 'true');
    if (request.view) queryParams.append('view', request.view);

    const response = await fetch(
      `${getApiV1Url(`/projects/${projectCode}/shops`)}?${queryParams}`,
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('API 응답 원본:', result);
    
    if (result.data) {
      return {
        totalCount: result.data.totalCount,
        shops: result.data.shops || [],
        totalPages: result.data.totalPages,
        currentPage: result.data.currentPage,
        pageSize: result.data.pageSize,
        hasNext: result.data.hasNext,
        hasPrevious: result.data.hasPrevious
      };
    }
    
    return result;
  } catch (error: any) {
    console.error('매장목록 조회 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 매장 상세 조회 API
export const getShopDetail = async (
  projectCode: string,
  shopCode: string
): Promise<ShopDetail> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/shops/${shopCode}`),
      {
        method: 'GET',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('매장 상세 API 응답:', result);
    
    return result.data;
  } catch (error: any) {
    console.error('매장 상세 조회 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 매장 수정 API
export const updateShop = async (
  projectCode: string,
  shopCode: string,
  shopData: ShopUpdateRequest
): Promise<ShopUpdateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/shops/${shopCode}`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(shopData)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return {
      success: true,
      message: '매장이 성공적으로 수정되었습니다.',
      data: result
    };
  } catch (error: any) {
    console.error('매장 수정 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

// 매장 상태별 건수 조회 API
// GET /api/v1/projects/{projectCode}/shop-stats/count/status
export const getShopStatusCount = async (
  projectCode: string
): Promise<ShopStatusCount> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/shop-stats/count/status`),
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    const data = (result?.data ?? result ?? {}) as Record<string, unknown>;
    const counts = parseApiCountMap(data.counts ?? data);

    const active = counts.operating ?? counts.operation ?? counts.ACTIVE ?? 0;
    const inactive = counts.closed ?? counts.INACTIVE ?? 0;
    const totalCount =
      Object.keys(counts).length > 0
        ? Object.values(counts).reduce((sum, v) => sum + v, 0)
        : active + inactive;

    return {
      count: totalCount,
      ACTIVE: active,
      INACTIVE: inactive,
    };
  } catch (error: any) {
    console.error('매장 상태별 건수 조회 중 오류 발생:', error);

    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }

    throw error;
  }
};

// ============================================================================
// FILE APIs (대표 이미지 · 리워드 카드)
// ============================================================================

export interface ShopFileCreateRequest {
  type: string;
  sortOrder: string;
  tempKey: string;
  tempFileName: string;
  originalName: string;
  contentType: string;
  declaredFileSize: number;
}

const getShopApiHeaders = (): Record<string, string> => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  return {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
};

export const toShopPrimaryFileRequest = (
  uploadFile: UploadFilePayload
): ShopFileCreateRequest => ({
  type: 'PRIMARY',
  sortOrder: generateAfter(null),
  tempKey: uploadFile.tempKey,
  tempFileName: uploadFile.tempFileName,
  originalName: uploadFile.originalName,
  contentType: uploadFile.contentType,
  declaredFileSize: uploadFile.declaredFileSize,
});

export const toShopRewardCardFileRequest = (
  uploadFile: UploadFilePayload
): ShopFileCreateRequest => ({
  type: 'REWARD_CARD',
  sortOrder: generateAfter(null),
  tempKey: uploadFile.tempKey,
  tempFileName: uploadFile.tempFileName,
  originalName: uploadFile.originalName,
  contentType: uploadFile.contentType,
  declaredFileSize: uploadFile.declaredFileSize,
});

/** 매장 대표 파일 썸네일 스토리지 URI */
export const getShopPrimaryThumbnailStorageUri = (
  projectCode: string,
  shopCode: string
): string => `/files/${projectCode}/shop/thumb/${shopCode}.webp`;

/** 매장 리워드 카드 파일 스토리지 URI */
export const getShopRewardCardThumbnailStorageUri = (
  projectCode: string,
  shopCode: string
): string => `/files/${projectCode}/reward-card/${shopCode}.webp`;

/** 매장 대표 파일 표시 URL (CDN 설정 시 CDN+?v= / 없으면 presign) */
export const resolveShopPrimaryUrl = async (
  projectCode: string,
  shopCode: string,
  primaryVersion?: number | null,
  updatedAt?: string | number | null
): Promise<string | null> =>
  resolveStoredFileUrl(getShopPrimaryThumbnailStorageUri(projectCode, shopCode), {
    cacheVersion: toCdnCacheVersion(primaryVersion, updatedAt),
  });

/**
 * 매장 목록용 — 썸네일 키(`/files/.../shop/thumb/{shopCode}.webp`)로
 * CDN이면 `?v=primaryVersion` 또는 `updatedAt` 타임스탬프, 아니면 pressign-download.
 */
export const resolveShopPrimaryUrls = async (
  projectCode: string,
  shops: Array<{
    code: string;
    primaryVersion?: number | null;
    updatedAt?: string | number | null;
  }>
): Promise<Array<string | null>> => {
  if (shops.length === 0) return [];

  const uris = shops.map((shop) => getShopPrimaryThumbnailStorageUri(projectCode, shop.code));
  const cdnBase = getCdnBaseUrl();

  if (cdnBase) {
    return shops.map((shop, i) => {
      const uri = uris[i].startsWith('/') ? uris[i] : `/${uris[i]}`;
      return appendCacheVersionQuery(
        `${cdnBase}${uri}`,
        toCdnCacheVersion(shop.primaryVersion, shop.updatedAt)
      );
    });
  }

  const downloads = await requestPresignedDownloads(uris);
  const urlByUri = new Map<string, string>();
  for (const item of downloads) {
    if (!item.presignedUrl) continue;
    const key = item.s3Uri?.startsWith('/')
      ? item.s3Uri
      : item.s3Uri
        ? `/${item.s3Uri}`
        : '';
    if (key) {
      urlByUri.set(key, item.presignedUrl);
    }
  }

  return uris.map((uri, index) => {
    const normalized = uri.startsWith('/') ? uri : `/${uri}`;
    return urlByUri.get(normalized) ?? downloads[index]?.presignedUrl ?? null;
  });
};

/** 매장 리워드 카드 파일 표시 URL */
export const resolveShopRewardCardUrl = async (
  projectCode: string,
  shopCode: string,
  rewardCardVersion?: string | number | null,
  updatedAt?: string | number | null
): Promise<string | null> =>
  resolveStoredFileUrl(getShopRewardCardThumbnailStorageUri(projectCode, shopCode), {
    cacheVersion: toCdnCacheVersion(rewardCardVersion, updatedAt),
  });

/** 매장 파일 등록 */
export const createShopFile = async (
  projectCode: string,
  shopCode: string,
  request: ShopFileCreateRequest
): Promise<void> => {
  try {
    const url = getApiV1Url(`/projects/${projectCode}/shops/${shopCode}/files`);
    console.log('[매장 파일 등록] API 요청', { url, body: request });

    const response = await fetch(url, {
      method: 'POST',
      headers: getShopApiHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result = await response.json();
    console.log('매장 파일 등록 성공 - 서버 응답:', result);
  } catch (error: unknown) {
    console.error('매장 파일 등록 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as Error & { isNetworkError?: boolean }).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/** 매장 대표 파일 삭제 */
export const deleteShopPrimary = async (
  projectCode: string,
  shopCode: string
): Promise<void> => {
  try {
    const url = getApiV1Url(
      `/projects/${projectCode}/shops/${shopCode}/files/primary/delete`
    );
    console.log('[매장 대표 파일 삭제] API 요청', { url, method: 'POST' });

    const response = await fetch(url, {
      method: 'POST',
      headers: getShopApiHeaders(),
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result = await response.json();
    console.log('매장 대표 파일 삭제 성공 - 서버 응답:', result);
  } catch (error: unknown) {
    console.error('매장 대표 파일 삭제 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as Error & { isNetworkError?: boolean }).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/** 매장 리워드 카드 파일 삭제 */
export const deleteShopRewardCard = async (
  projectCode: string,
  shopCode: string
): Promise<void> => {
  try {
    const url = getApiV1Url(
      `/projects/${projectCode}/shops/${shopCode}/files/reward-card/delete`
    );
    console.log('[매장 리워드 카드 파일 삭제] API 요청', { url, method: 'POST' });

    const response = await fetch(url, {
      method: 'POST',
      headers: getShopApiHeaders(),
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(CommonUtils.getHttpErrorMessage(statusCode, errorData));
    }

    const result = await response.json();
    console.log('매장 리워드 카드 파일 삭제 성공 - 서버 응답:', result);
  } catch (error: unknown) {
    console.error('매장 리워드 카드 파일 삭제 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as Error & { isNetworkError?: boolean }).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};